import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  /** Bezeichnung des isolierten Blocks fuer den Fallback-Text — Modul-Name, Widget-Slug, o.ae. */
  label: string;
  /** Wenn 'widget': kompakter Fallback fuer einen Widget-Embed. Default 'module': groesserer Modul-Fallback. */
  scope?: 'module' | 'widget';
  children: ReactNode;
}

interface State {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/** Faengt Render-/Lifecycle-Crashes in einer isolierten Modul- oder Widget-Subtree ab,
 *  damit ein Bug in einem Modul nicht das ganze Portal weiss-macht.
 *
 *  Kein Reporting nach extern (noch kein Sentry o.ae.) — Stack landet im Browser-Console
 *  und kann via Detail-Toggle eingeblendet werden. Reset-Button rendert den Subtree neu;
 *  bleibt der Fehler deterministisch, faellt er sofort wieder in den Fallback. */
export default class ModuleErrorBoundary extends Component<Props, State> {
  state: State = { error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    // Console-Log fuer Diagnose; spaeter ggf. an Telemetrie-Endpunkt schicken
    // eslint-disable-next-line no-console
    console.error(`[ModuleErrorBoundary:${this.props.label}]`, error, errorInfo);
  }

  reset = () => {
    this.setState({ error: null, errorInfo: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      const isWidget = this.props.scope === 'widget';
      const compact = isWidget;
      return (
        <div
          className={`flex flex-col items-center justify-center gap-2 text-center ${
            compact ? 'p-4 min-h-[120px]' : 'p-8 min-h-[40vh]'
          } rounded-lg border border-amber-500/30 bg-amber-500/5`}
        >
          <AlertTriangle className={compact ? 'w-5 h-5' : 'w-8 h-8'} aria-hidden="true" />
          <div className={compact ? 'text-sm font-medium' : 'text-base font-semibold'}>
            {isWidget ? 'Widget' : 'Modul'} „{this.props.label}" temporär nicht verfügbar
          </div>
          <div className={`text-muted-foreground ${compact ? 'text-xs' : 'text-sm'} max-w-md`}>
            Ein unerwarteter Fehler hat diesen Bereich angehalten. Die übrigen
            {isWidget ? ' Widgets' : ' Module'} sind weiterhin nutzbar.
          </div>
          <button
            type="button"
            onClick={this.reset}
            className={`inline-flex items-center gap-1.5 mt-1 rounded ${
              compact ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
            } bg-amber-500/20 text-amber-100 hover:bg-amber-500/30 transition-colors`}
          >
            <RefreshCw className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} /> Neu laden
          </button>
          {this.state.error.message && (
            <details className={`mt-2 ${compact ? 'text-[10px]' : 'text-xs'} text-muted-foreground max-w-full`}>
              <summary className="cursor-pointer">Details</summary>
              <pre className="mt-1 text-left whitespace-pre-wrap break-words opacity-80">
                {this.state.error.message}
              </pre>
            </details>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
